import React, { useState } from "react";
import { StyleSheet, View, TouchableOpacity, ActivityIndicator } from "react-native";
import { ThemedText } from "../ThemedText";
import { ThemedView } from "../ThemedView";
import { TaskModel } from "common247";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ButtonPaddings } from "@/constants/themes/components";

interface ITaskCardProps {
  task: TaskModel;
  onAction: (taskId: string, action: "accept" | "reject") => Promise<void>;
}

/**
 * TaskCard displays details of a state-machine driven workflow task (Review or Revision).
 * Allows the user to Accept or Reject the task with appropriate forest-emerald and danger styled buttons.
 * @param props - The component properties containing the task and action handler.
 * @param props.task
 * @param props.onAction
 */
export function TaskCard({ task, onAction }: ITaskCardProps) {
  const [loadingAction, setLoadingAction] = useState<"accept" | "reject" | null>(null);

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMutedColor = useThemeColor({}, "textMuted");

  const handleAction = async (action: "accept" | "reject") => {
    if (!task.id || loadingAction !== null) return;
    setLoadingAction(action);
    try {
      await onAction(task.id, action);
    } finally {
      setLoadingAction(null);
    }
  };

  const isRevision = task.state === "draft";
  const isContactLink = task.taskType === "contact_link" || Boolean(task.contactName);
  const dateFormatted = new Date(task.createdAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const badgeColor = isContactLink ? tintColor : (isRevision ? "#F59E0B" : tintColor);
  const badgeBg = isContactLink
    ? "rgba(5, 150, 105, 0.15)"
    : isRevision
    ? "rgba(245, 158, 11, 0.15)"
    : "rgba(5, 150, 105, 0.15)";
  const badgeIcon = isContactLink
    ? "person-add-outline"
    : isRevision
    ? "git-compare-outline"
    : "document-text-outline";
  const badgeLabel = isContactLink
    ? "Contact Link Request"
    : isRevision
    ? "Revision Needed"
    : "Review Request";

  return (
    <ThemedView style={[styles.card, { backgroundColor: cardBgColor, borderColor }]}>
      {/* Badge and Date Header */}
      <View style={styles.headerRow}>
        <View
          style={[
            styles.badge,
            {
              backgroundColor: badgeBg,
            },
          ]}
        >
          <Ionicons name={badgeIcon as any} size={12} color={badgeColor} style={styles.badgeIcon} />
          <ThemedText style={[styles.badgeText, { color: badgeColor }]}>{badgeLabel}</ThemedText>
        </View>
        <ThemedText style={[styles.dateText, { color: textMutedColor }]}>{dateFormatted}</ThemedText>
      </View>

      {/* Main Content */}
      <View style={styles.contentBody}>
        <ThemedText type="subtitle" style={styles.titleText}>
          {task.title}
        </ThemedText>
        <ThemedText style={[styles.descriptionText, { color: textMutedColor }]}>{task.description}</ThemedText>

        {/* Context Box (Contact Link or Memory) */}
        {isContactLink ? (
          <View style={[styles.contextBox, { borderColor }]}>
            <Ionicons name="person-outline" size={16} color={tintColor} style={styles.contextIcon} />
            <View style={styles.contextTextContainer}>
              <ThemedText style={[styles.contextLabel, { color: textMutedColor }]}>Contact to Link</ThemedText>
              <ThemedText type="defaultSemiBold" style={styles.contextValue}>
                {task.contactName || task.contactDisplayName || "Matching Contact"}
                {task.targetUserEmail ? ` (${task.targetUserEmail})` : ""}
              </ThemedText>
            </View>
          </View>
        ) : task.memoryTitle ? (
          <View style={[styles.contextBox, { borderColor }]}>
            <Ionicons name="images-outline" size={16} color={tintColor} style={styles.contextIcon} />
            <View style={styles.contextTextContainer}>
              <ThemedText style={[styles.contextLabel, { color: textMutedColor }]}>Memory</ThemedText>
              <ThemedText type="defaultSemiBold" style={styles.contextValue}>
                {task.memoryTitle}
              </ThemedText>
            </View>
          </View>
        ) : null}
      </View>

      {/* Action Buttons */}
      <View style={styles.actionsRow}>
        <TouchableOpacity activeOpacity={0.8} style={[styles.btn, styles.btnReject]} onPress={() => handleAction("reject")} disabled={loadingAction !== null}>
          {loadingAction === "reject" ? (
            <ActivityIndicator size="small" color="#EF4444" />
          ) : (
            <>
              <Ionicons name="close-circle-outline" size={16} color="#EF4444" style={styles.btnIcon} />
              <ThemedText style={styles.btnRejectText}>{isRevision ? "Cancel Loop" : "Reject"}</ThemedText>
            </>
          )}
        </TouchableOpacity>

        <TouchableOpacity activeOpacity={0.8} style={[styles.btn, styles.btnAccept, { backgroundColor: tintColor }]} onPress={() => handleAction("accept")} disabled={loadingAction !== null}>
          {loadingAction === "accept" ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <>
              <Ionicons name="checkmark-circle-outline" size={16} color="#FFFFFF" style={styles.btnIcon} />
              <ThemedText style={styles.btnAcceptText}>{isRevision ? "Resubmit" : "Accept"}</ThemedText>
            </>
          )}
        </TouchableOpacity>
      </View>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 20,
    borderWidth: 1,
    padding: 18,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: ButtonPaddings.standard.paddingHorizontal,
    paddingVertical: ButtonPaddings.standard.paddingVertical,
    borderRadius: 99,
  },
  badgeIcon: {
    marginRight: 4,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  dateText: {
    fontSize: 11,
    fontWeight: "500",
  },
  contentBody: {
    marginBottom: 16,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 6,
    letterSpacing: -0.2,
  },
  descriptionText: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  contextBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "rgba(150, 150, 150, 0.03)",
  },
  contextIcon: {
    marginRight: 10,
  },
  contextTextContainer: {
    flex: 1,
  },
  contextLabel: {
    fontSize: 9,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 1,
  },
  contextValue: {
    fontSize: 13,
  },
  actionsRow: {
    flexDirection: "row",
    gap: 10,
  },
  btn: {
    flex: 1,
    flexDirection: "row",
    height: 40,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  btnIcon: {
    marginRight: 5,
  },
  btnReject: {
    borderWidth: 1.5,
    borderColor: "#EF4444",
    backgroundColor: "transparent",
  },
  btnRejectText: {
    color: "#EF4444",
    fontSize: 13,
    fontWeight: "600",
  },
  btnAccept: {
    shadowColor: "#059669",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 2,
  },
  btnAcceptText: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "600",
  },
});
